import axios from 'axios';
import { API_URL } from '../config';
import {
    InitiateStoryResponse,
    AfterRollRequest,
    AfterRollResponse,
    PlayActionResponse,
    SelectUpgradeResponse,
    PlayParadoxResponse,
} from '../types';

/**
 * Call /continue-story/initiate endpoint
 * This initiates a branching story continuation with dice mechanics.
 * Used for post-tutorial gameplay only.
 */
export const initiateStoryContinuation = async (
    sessionId: string,
    cardName: string,
    actionPhrase: string
): Promise<InitiateStoryResponse> => {
    const response = await axios.post<InitiateStoryResponse>(
        `${API_URL}/story/continue-story/initiate`,
        {
            session_id: sessionId,
            card_name: cardName,
            action_phrase: actionPhrase
        }
    );
    return response.data;
};

/**
 * Call /continue-story/after-roll endpoint
 * This fetches the story branch based on the dice roll result.
 * Should be called after the dice animation completes.
 */
export const fetchAfterRollStory = async (
    sessionId: string,
    lastSectionId: number
): Promise<AfterRollResponse> => {
    const request: AfterRollRequest = {
        session_id: sessionId,
        last_section_id: lastSectionId
    };

    const response = await axios.post<AfterRollResponse>(
        `${API_URL}/story/continue-story/after-roll`,
        request
    );
    return response.data;
};

/**
 * Call /play-action endpoint
 * Fast endpoint for card play that increments usage and checks for upgrades.
 * Called BEFORE /continue-story/initiate.
 */
export const playCardAction = async (
    sessionId: string,
    cardName: string
): Promise<PlayActionResponse> => {
    const response = await axios.post<PlayActionResponse>(
        `${API_URL}/play-action`,
        {
            session_id: sessionId,
            card_name: cardName
        }
    );
    return response.data;
};

/**
 * Call /select-upgrade endpoint
 * Submit the player's upgrade selection.
 */
export const selectUpgrade = async (
    sessionId: string,
    selectedCardName: string
): Promise<SelectUpgradeResponse> => {
    const response = await axios.post<SelectUpgradeResponse>(
        `${API_URL}/select-upgrade`,
        {
            session_id: sessionId,
            selected_card_name: selectedCardName
        }
    );
    return response.data;
};

/**
 * Call /play-paradox endpoint
 * Play a paradox or junk card to add its effects to the modifier stack.
 * Called before playing a verb card. Can be called multiple times per turn.
 */
export const playParadoxCard = async (
    sessionId: string,
    cardName: string
): Promise<PlayParadoxResponse> => {
    const response = await axios.post<PlayParadoxResponse>(
        `${API_URL}/play-paradox`,
        {
            session_id: sessionId,
            card_name: cardName
        }
    );
    return response.data;
};
